import React, { useEffect, useState, useContext, useMemo } from 'react';
import { CommonDataContext } from '../../firebase/CommonDataContext';
import { FirebaseNewContext } from '../../firebase/FirebaseNewContext';
import { UserDataContext } from '../../firebase/UserDataContext';
import { GlobalSettingsContext } from '../../firebase/GlobalSettingsContext';
import EventSection from './EventSection';
import { Box, Typography } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { useMemo as useMemoHook } from 'react';
import { CheckBox, Clear } from '@mui/icons-material';
import PropTypes from 'prop-types';
import * as ROUTES from '../../routes/routes';

// Helper to build links for scoring actions (same approach as ScoreCommitteeDashboardV2)
function buildScoringLinks({ entry, competition, event, team }) {
    const result = { editHref: null, viewHref: null, isEntered: !!entry?.entered };

    if (result.isEntered && entry?.score?.uid) {
        result.editHref = ROUTES.routeReplace(ROUTES.SCORING_EDIT, {
            editScore: entry.score.uid,
            competition
        });

        result.viewHref = ROUTES.routeReplace(ROUTES.SCORING_VIEW, {
            scoreUid: entry.score.uid,
            competition
        });
    } else {
        // not entered yet
        result.editHref = ROUTES.routeReplace(ROUTES.SCORING_FOR_EVENT_FOR_TEAM, {
            eventUid: event.eventUid,
            teamUid: team.teamUid
        });
    }

    return result;
}

const EventActionCell = (params) => {
    const { entry, competition, event, team } = params.value || {};
    const links = buildScoringLinks({ entry, competition, event, team });

    return (
        <>
            <a href={links.editHref} target="_blank" rel="noopener noreferrer" style={{ marginRight: 10 }}>
                {links.isEntered ? 'edit score' : 'enter new score'}
            </a>
            {links.isEntered && links.viewHref && (
                <a href={links.viewHref} target="_blank" rel="noopener noreferrer" style={{ marginRight: 8 }}>
                    view score
                </a>
            )}
        </>
    );
};

const eventColumns = [
    { field: 'col1', headerName: 'Team Number', width: 150, sortComparator: (v1, v2) => Number(v1) - Number(v2) },
    { field: 'col2', headerName: 'Team Name', width: 300 },
    { field: 'col3', headerName: 'Category', width: 150 },
    { field: 'col4', headerName: 'Division', width: 150 },
    { field: 'col5', headerName: 'Has been entered', width: 150, renderCell: (params) => (params.value ? <CheckBox /> : <Clear />) },
    { field: 'col6', headerName: 'Final Score', width: 150, getApplyQuickFilterFn: () => null },
    { field: 'col7', headerName: 'Action', width: 250, renderCell: EventActionCell }
];

const JudgeDashboardNew = (props) => {
    const { firebase } = useContext(FirebaseNewContext);
    const { globalSettings } = useContext(GlobalSettingsContext);
    const { defaultSelectedCompetition, user } = useContext(UserDataContext);
    const { eventsMap: globalEventsMap, teamsMap: globalTeamsMap } = useContext(CommonDataContext);

    const eventsMap = useMemo(() => globalEventsMap[defaultSelectedCompetition] || {}, [defaultSelectedCompetition, globalEventsMap]);
    const teamsMap = useMemo(() => globalTeamsMap[defaultSelectedCompetition] || {}, [defaultSelectedCompetition, globalTeamsMap]);
    const categoriesMap = useMemo(() => (globalSettings?.categories?.[defaultSelectedCompetition]) || {}, [defaultSelectedCompetition, globalSettings]);
    const divisionsMap = useMemo(() => (globalSettings?.divisions?.[defaultSelectedCompetition]) || {}, [defaultSelectedCompetition, globalSettings]);

    const [scoresMap, setScoresMap] = useState({});

    useEffect(() => {
        const fetch = async () => {
            try {
                const scoresMapResult = await firebase.getScores(defaultSelectedCompetition);
                setScoresMap(scoresMapResult || {});
            } catch (e) {
                console.error('Failed to load scores for judge dashboard', e);
                setScoresMap({});
            }
        };
        fetch().catch(console.error);
    }, [firebase, defaultSelectedCompetition]);

    // Build a map of events -> teams with entry info, but only include events assigned to this judge
    const eventKeysWithTeams = useMemo(() => {
        const acc = {};
        const judgeEvents = user?.eventsNew || false;
        const judgeEventUidList = judgeEvents ? judgeEvents[defaultSelectedCompetition] : [];

        Object.keys(eventsMap || {}).forEach((eventUid) => {
            if (!(judgeEventUidList && judgeEventUidList.includes(eventUid))) return;

            acc[eventUid] = Object.keys(teamsMap || {}).reduce((tAcc, tUid) => {
                tAcc[tUid] = { ...teamsMap[tUid], entered: false };
                return tAcc;
            }, {});
        });

        // annotate with scores
        Object.keys(scoresMap || {}).forEach((scoreUid) => {
            const score = { uid: scoreUid, ...scoresMap[scoreUid] };
            if (score.eventUid && acc[score.eventUid] && acc[score.eventUid][score.teamUid]) {
                acc[score.eventUid][score.teamUid].entered = true;
                acc[score.eventUid][score.teamUid].score = score;
            }
        });

        return acc;
    }, [eventsMap, teamsMap, scoresMap, user, defaultSelectedCompetition]);

    const eventKeys = Object.keys(eventKeysWithTeams);

    return (
        <Grid2 container direction="column" sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, width: '100%' }}>
            <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }} >Judge Dashboard</Typography>

            {eventKeys.length === 0 && (
                <Typography variant="body1">No Events Assigned</Typography>
            )}

            {eventKeys.map((eKey) => {
                const event = eventsMap[eKey] || {};
                const eventRows = Object.keys(eventKeysWithTeams[eKey] || {}).map((t) => {
                    const team = teamsMap[t] || {};
                    const entry = eventKeysWithTeams[eKey][t] || {};
                    return {
                        id: `${eKey}-${t}`,
                        col1: team?.teamNumber || '',
                        col2: team?.teamName || '',
                        col3: categoriesMap[team?.category] || '',
                        col4: divisionsMap[team?.division] || '',
                        col5: !!entry.entered,
                        col6: entry.entered ? entry.score?.finalScore ?? '-' : '-',
                        col7: { entry, competition: defaultSelectedCompetition, event: { ...event, eventUid: eKey }, team: { ...team, teamUid: t } }
                    };
                });

                return (
                    <EventSection key={eKey} event={event} eventRows={eventRows} eventColumns={eventColumns} />
                );
            })}
        </Grid2>
    );
};

JudgeDashboardNew.propTypes = {
    competition: PropTypes.string
};

export default JudgeDashboardNew;
